/** Shorten an id or token for display (never the full URL as primary text). */
export const shortenToken = (s) => {
  if (!s || typeof s !== 'string') return '';
  const t = s.trim();
  if (t.length <= 10) return t;
  return `${t.slice(0, 4)}…${t.slice(-4)}`;
};

export const formatRecentTime = (ts) => {
  if (typeof ts !== 'number' || !Number.isFinite(ts)) return '';
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 45) return 'Just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  try {
    return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

/**
 * Friendly labels for a saved URL (no API — parse path/query only).
 * @returns {{ title: string, detail: string }}
 */
export const describeMusicRecent = (url, source) => {
  const brand = source === 'spotify' ? 'Spotify' : 'YouTube';
  try {
    const u = new URL(url.trim());

    if (source === 'spotify') {
      const m = u.pathname.match(/^\/(playlist|album|track|artist|episode|show)\/([a-zA-Z0-9]+)/);
      if (!m) {
        return { title: 'Spotify link', detail: brand };
      }
      const labels = {
        playlist: 'Playlist',
        album: 'Album',
        track: 'Track',
        artist: 'Artist',
        episode: 'Episode',
        show: 'Show',
      };
      const kind = labels[m[1]] || 'Link';
      return { title: kind, detail: `${brand} · ${shortenToken(m[2])}` };
    }

    /* YouTube */
    const shorts = u.pathname.match(/^\/shorts\/([a-zA-Z0-9_-]+)/);
    if (shorts) {
      return { title: 'Video', detail: `${brand} · ${shortenToken(shorts[1])}` };
    }
    if (u.hostname === 'youtu.be') {
      const id = u.pathname.replace(/^\//, '').split('/')[0];
      if (id) return { title: 'Video', detail: `${brand} · ${shortenToken(id)}` };
    }
    const v = u.searchParams.get('v');
    const list = u.searchParams.get('list');
    if (list && !v) {
      return { title: 'Playlist', detail: `${brand} · ${shortenToken(list)}` };
    }
    if (v) {
      return { title: 'Video', detail: `${brand} · ${shortenToken(v)}` };
    }
    return { title: 'YouTube link', detail: brand };
  } catch {
    return {
      title: source === 'spotify' ? 'Spotify link' : 'YouTube link',
      detail: brand,
    };
  }
};

/**
 * Single-line label for a saved recent (uses oEmbed titles when present).
 * @param {{ url: string, source: string, ts: number, spotifyTitle?: string, youtubeTitle?: string }} entry
 */
export const formatMusicRecentDisplayLine = (entry) => {
  const rel = formatRecentTime(entry.ts);
  if (entry.source === 'spotify' && entry.spotifyTitle?.trim()) {
    const t = entry.spotifyTitle.trim();
    return rel ? `${t} · ${rel}` : t;
  }
  if (entry.source === 'youtube' && entry.youtubeTitle?.trim()) {
    const t = entry.youtubeTitle.trim();
    return rel ? `${t} · ${rel}` : t;
  }
  const { title, detail } = describeMusicRecent(entry.url, entry.source);
  const sub = rel ? `${detail} · ${rel}` : detail;
  return `${title} · ${sub}`;
};
